import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutline from '@mui/icons-material/DeleteOutlineOutlined';
import Add from '@mui/icons-material/Add';
import UndoOutlined from '@mui/icons-material/UndoOutlined';
import { api } from './api';
import {
  amount,
  AccessoryPanel,
  type LineAccessory,
  decimalInput,
  money,
  plain,
  PRICE_DECIMALS,
  QTY_DECIMALS,
  skuPicker,
  Tag,
} from './ui';

interface Props {
  order: any;
  /** Only the supplier may change prices; the buyer adjusts quantities and dates. */
  seller: boolean;
  /** The seller keeps a stock book, so new quantities are checked against what it can ship. */
  onClose: () => void;
  onDone: () => void;
}

/**
 * Edits every line of an unshipped order, removes or adds items, and submits it as a new
 * version. Added items follow the new-order rules: the supplier's quote when the buyer adds
 * them, the seller's own price otherwise, and the buyer's mapping for receiving.
 */
export default function ReviseDialog({ order, seller, onClose, onDone }: Props) {
  // Products only: their accessories follow them and are shown under each, not edited.
  const [lines, setLines] = useState(() =>
    order.lines.map((l: any) => ({
      id: l.id,
      skuId: l.seller_sku_id,
      name: l.sku_snapshot.name,
      spec: l.sku_snapshot.spec,
      unit: l.sku_snapshot.unit,
      quantity: plain(l.quantity, QTY_DECIMALS),
      unitPrice: plain(l.unit_price, PRICE_DECIMALS),
      // Kept while the agreed price stays; a new price drops the material adjustment.
      agreedPrice: l.unit_price,
      adjustRate: l.adjust_rate,
      removed: false,
      isNew: false,
      accessories: l.accessories as LineAccessory[],
    })),
  );
  // The supplier's catalog: items to add, and what goes out with each new product.
  const [catalog, setCatalog] = useState<any[] | null>(null);
  useEffect(() => {
    api<any[]>(`/catalog/${order.seller_id}`)
      .then(setCatalog)
      .catch((e) => setError(e.message));
  }, [order.seller_id]);
  const addRow = () => {
    setLines((all: any[]) => [
      ...all,
      {
        id: `new-${crypto.randomUUID()}`,
        skuId: '',
        name: '',
        spec: '',
        unit: '',
        quantity: '',
        unitPrice: '',
        removed: false,
        isNew: true,
      },
    ]);
  };
  const pick = (id: string, item: any) =>
    update(id, {
      skuId: item?.id ?? '',
      name: item?.name ?? '',
      spec: item?.spec ?? '',
      unit: item?.unit ?? '',
      // The buyer pays the supplier's quote; the seller starts from it and may change it.
      unitPrice: item?.sales_price != null ? plain(item.sales_price, PRICE_DECIMALS) : '',
    });
  const catalogItem = (l: any) => catalog?.find((item) => item.id === l.skuId);
  /**
   * What goes out with a product line: an existing line keeps the accessories agreed on the
   * order; a new product brings the supplier's current ones. Quantities follow the product.
   */
  const accessoriesOf = (l: any): LineAccessory[] =>
    l.isNew ? (catalogItem(l)?.bundle ?? []) : (l.accessories ?? []);
  // delivery_date arrives as a UTC timestamp of local midnight; read it back in local time.
  const [deliveryDate, setDeliveryDate] = useState(() =>
    new Date(order.delivery_date).toLocaleDateString('sv-SE'),
  );
  const [note, setNote] = useState(order.note ?? '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Qualified stock the seller can still commit, per item, summed over warehouses.
  const [available, setAvailable] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    if (!seller) return;
    api<any[]>('/inventory')
      .then((rows) => {
        const totals: Record<string, number> = {};
        for (const r of rows)
          if (r.quality === 'AVAILABLE')
            totals[r.sku_id] = (totals[r.sku_id] ?? 0) + Number(r.available);
        setAvailable(totals);
      })
      // The server re-checks on submit, so a failed preview only hides the hint.
      .catch(() => setAvailable(null));
  }, [seller]);
  const stockOf = (l: any) => (available ? (available[l.skuId] ?? 0) : null);
  const short = (l: any) => {
    const stock = stockOf(l);
    return stock !== null && Number(l.quantity) > stock;
  };
  // Removed items leave the order, so they neither count towards the total nor need stock.
  const kept = lines.filter((l: any) => !l.removed);
  const shortCount = kept.filter(short).length;

  const added = kept.filter((l: any) => l.isNew);
  // Problems with added items that only the user can fix, shown before submitting.
  const addIssue = added
    .map((l: any) => {
      const item = catalogItem(l);
      if (!item) return '';
      if (!seller && item.sales_price == null) return `${item.name} 供应商尚未报价，不能加入订单。`;
      if (!seller && item.mapping_ready === false)
        return `${item.name} 没有可自动对应的同名采购物料，请先在“往来单位”中设置到货入库的我方商品。`;
      return '';
    })
    .find(Boolean);
  const update = (id: string, patch: Record<string, string | boolean>) =>
    setLines((all: any[]) => all.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  /** The material adjustment a line would carry, by the same rules as the server. */
  const rateOf = (l: any): string => {
    if (!l.isNew) return Number(l.unitPrice) === Number(l.agreedPrice) ? l.adjustRate : '0';
    const item = catalogItem(l);
    return item?.adjust && Number(l.unitPrice) === Number(item.sales_price)
      ? item.adjust.rate
      : '0';
  };
  const total = kept.reduce(
    (sum: number, l: any) =>
      sum + (Number(l.quantity) * Number(l.unitPrice) * (1 + Number(rateOf(l))) || 0),
    0,
  );
  const before = order.lines.reduce(
    (sum: number, l: any) =>
      sum + Number(l.quantity) * Number(l.unit_price) * (1 + Number(l.adjust_rate)),
    0,
  );
  const adjustment = kept.reduce(
    (sum: number, l: any) =>
      sum + (Number(l.quantity) * Number(l.unitPrice) * Number(rateOf(l)) || 0),
    0,
  );

  async function submit() {
    if (busy) return;
    if (!kept.length) return setError('订单至少需要保留一个商品；如整单不要了，请取消订单');
    if (added.some((l: any) => !l.skuId)) return setError('请为新增的商品选择具体商品，或删除空行');
    if (kept.some((l: any) => !(Number(l.quantity) > 0) || l.unitPrice === ''))
      return setError('保留的商品都需要有效的数量和单价；不需要的商品请移除');
    if (!deliveryDate) return setError('请选择交付日期');
    setBusy(true);
    setError('');
    try {
      await api(`/orders/${order.id}/revise`, {
        version: order.version,
        deliveryDate,
        note,
        // Products only: the server moves each one's accessories with it.
        lines: kept
          .filter((l: any) => !l.isNew)
          .map((l: any) => ({ id: l.id, quantity: l.quantity, unitPrice: l.unitPrice })),
        added: added.map((l: any) => ({
          sellerSkuId: l.skuId,
          quantity: l.quantity,
          // The buyer's price is the supplier's quote, re-checked by the server.
          unitPrice: l.unitPrice,
          adjustRate: rateOf(l),
        })),
      });
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const removedCount = lines.filter((l: any) => l.removed).length;

  return (
    <Dialog open fullWidth maxWidth="md" onClose={() => !busy && onClose()}>
      <DialogTitle sx={{ pb: 0.5 }}>
        变更约定
        <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
          {order.number} · v{order.version} → v{order.version + 1}，提交后需对方重新确认
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1.5 }}>
          {(error || addIssue || shortCount > 0) && (
            <Alert severity={error ? 'error' : 'warning'}>
              {error ||
                addIssue ||
                `${shortCount} 种商品超过可用库存，变更后无法发货，请调低数量或移除。`}
            </Alert>
          )}

          {/* Items: an MUI table; the container clips its rounded corners over the border. */}
          <TableContainer sx={{ border: '1px solid #e7edef', borderRadius: 1, overflow: 'hidden' }}>
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                '& td, & th': { borderColor: '#edf1f2' },
                '& td': { py: 1.25 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>商品</TableCell>
                  <TableCell sx={{ width: 190 }}>数量</TableCell>
                  <TableCell sx={{ width: 120 }}>单价</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>
                    金额
                  </TableCell>
                  <TableCell sx={{ width: 56 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l: any) => {
                  const sum = Number(l.quantity) * Number(l.unitPrice);
                  const accessories = l.skuId ? accessoriesOf(l) : [];
                  const item = catalogItem(l);
                  const remove = () =>
                    l.isNew
                      ? setLines((all: any[]) => all.filter((x) => x.id !== l.id))
                      : update(l.id, { removed: true });
                  if (l.removed)
                    return (
                      <TableRow key={l.id}>
                        <TableCell>
                          <Typography
                            color="text.disabled"
                            sx={{ fontSize: 14, textDecoration: 'line-through' }}
                            noWrap
                          >
                            {l.name}
                          </Typography>
                        </TableCell>
                        <TableCell colSpan={3} sx={{ color: 'text.disabled', fontSize: 13 }}>
                          将从订单中移除
                        </TableCell>
                        {/* Same column and size as the delete button, so the row actions line up. */}
                        <TableCell align="center" sx={{ px: 0 }}>
                          <Tooltip title="撤销移除">
                            <IconButton
                              size="small"
                              aria-label="撤销移除"
                              disabled={busy}
                              onClick={() => update(l.id, { removed: false })}
                              sx={{ color: 'primary.main' }}
                            >
                              <UndoOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  return [
                    <TableRow
                      key={l.id}
                      hover
                      sx={{
                        '&:hover .row-remove': { opacity: 1 },
                        // A product and its accessories read as one group: no rule between them.
                        ...(accessories.length ? { '& td': { borderBottom: 0 } } : {}),
                      }}
                    >
                      <TableCell>
                        {l.isNew && !l.skuId ? (
                          <Autocomplete<any>
                            size="small"
                            autoFocus
                            openOnFocus
                            value={null}
                            options={catalog ?? []}
                            loading={!catalog}
                            disabled={busy}
                            {...skuPicker({
                              aside: (option) =>
                                option.sales_price != null &&
                                `¥${plain(option.sales_price, PRICE_DECIMALS)}/${option.unit}`,
                            })}
                            // An item already on the order is changed on its own line, not added twice.
                            getOptionDisabled={(option) =>
                              lines.some((x: any) => !x.removed && x.skuId === option.id)
                            }
                            onChange={(_, option) => option && pick(l.id, option)}
                            renderInput={(params) => (
                              <TextField {...params} placeholder="搜索要加入的商品" />
                            )}
                          />
                        ) : (
                          <>
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                            >
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                                {l.name}
                              </Typography>
                              {l.isNew && <Tag tone="info">新增</Tag>}
                            </Box>
                            <Typography color="text.secondary" sx={{ fontSize: 12 }} noWrap>
                              {/* For an added item the buyer also sees where it will be received. */}
                              {l.isNew && !seller && item?.mapping
                                ? `入库为我方「${item.mapping.buyer_name}」`
                                : l.spec}
                            </Typography>
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            size="small"
                            value={l.quantity}
                            disabled={busy || (l.isNew && !l.skuId)}
                            error={short(l)}
                            inputMode="decimal"
                            placeholder="0"
                            onChange={(e) =>
                              update(l.id, { quantity: decimalInput(e.target.value, QTY_DECIMALS) })
                            }
                            sx={{ flex: 1, '& input': { textAlign: 'right' } }}
                          />
                          <Typography color="text.secondary" sx={{ fontSize: 13, width: 16 }}>
                            {l.unit}
                          </Typography>
                        </Box>
                        {short(l) && (
                          <Typography color="error" sx={{ fontSize: 11.5, mt: 0.25 }}>
                            可用 {amount(stockOf(l))}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {seller ? (
                          <TextField
                            size="small"
                            value={l.unitPrice}
                            disabled={busy || (l.isNew && !l.skuId)}
                            inputMode="decimal"
                            placeholder="0.00"
                            onChange={(e) =>
                              update(l.id, {
                                unitPrice: decimalInput(e.target.value, PRICE_DECIMALS),
                              })
                            }
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <Typography color="text.secondary" sx={{ fontSize: 13, mr: 0.5 }}>
                                    ¥
                                  </Typography>
                                ),
                              },
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 14 }}>
                            {l.unitPrice === '' ? '—' : `¥${l.unitPrice}`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}
                      >
                        {sum > 0 ? `${money(sum)}` : '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ px: 0 }}>
                        <IconButton
                          className="row-remove"
                          size="small"
                          aria-label="移除这个商品"
                          disabled={busy || (kept.length === 1 && !l.isNew)}
                          onClick={remove}
                          sx={{ opacity: l.isNew ? 1 : 0.35, transition: 'opacity .15s' }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>,
                    // Shipped with the product as a whole; set on the product, shown here.
                    accessories.length > 0 && (
                      <TableRow key={`${l.id}-accessories`}>
                        <TableCell colSpan={5} sx={{ pt: 0, pb: 1.5 }}>
                          <AccessoryPanel
                            accessories={accessories}
                            productUnit={l.unit}
                            pieces={l.quantity}
                          />
                        </TableCell>
                      </TableRow>
                    ),
                  ];
                })}
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 0.5, borderBottom: 0 }}>
                    <Button
                      size="small"
                      startIcon={<Add />}
                      disabled={busy || kept.length >= 30 || added.some((l: any) => !l.skuId)}
                      onClick={addRow}
                    >
                      添加商品
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 2 }}>
            <TextField
              size="small"
              type="date"
              label="交付日期"
              value={deliveryDate}
              disabled={busy}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setDeliveryDate(e.target.value)}
            />
            <TextField
              size="small"
              label="变更说明（选填）"
              value={note}
              disabled={busy}
              onChange={(e) => setNote(e.target.value)}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ mr: 'auto', display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            {total > 0 ? `${money(total)}` : '—'}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
            {kept.length} 种商品
            {Math.abs(adjustment) >= 0.005 &&
              ` · 含原料调整 ${adjustment > 0 ? '+' : '-'}${money(Math.abs(adjustment))}`}
            {added.length > 0 && ` · 新增 ${added.length}`}
            {removedCount > 0 && ` · 移除 ${removedCount}`}
            {Math.abs(total - before) >= 0.005 &&
              ` · 较当前${total > before ? '增加' : '减少'} ${money(Math.abs(total - before))}`}
          </Typography>
        </Box>
        <Button disabled={busy} onClick={onClose}>
          取消
        </Button>
        <Button
          variant="contained"
          disabled={busy || shortCount > 0 || !!addIssue}
          onClick={submit}
        >
          {busy ? '正在提交…' : '提交变更'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
